import React, { useEffect, useReducer, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faKeyboard, faTimes } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../../shared/notify";

import Dropdown from "./components/dropdown";
import Loader from "../../loader/loader";
import NoOptions from "./components/no-options";
import { SortableList } from "../draggable-items/sortable-list";

import { SelectedItem } from "../shared/selected-item";
import { initialState, reducer } from "./reducers";
import { getProviderByTerm } from "../shared/data";
import { checkMultiContains, checkMultiMatch, checkSingleMatch } from "./lib/check-match";

import "./typeahead.scss";

const findOption = (optionText, options) => {
    if (!options) {
        return null;
    }

    return options.find((option) => option.text.toLowerCase() === optionText.toLowerCase()) || null;
};

const Typeahead = (props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const inputRef = useRef();
    const typeaheadRef = useRef();
    const dropdownRef = useRef();
    const [unmounted, setUnmounted] = useState(false);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        if (props.options && props.options.length) {
            dispatch({ type: "SET_OPTIONS", options: props.options });
        }

        document.addEventListener("mousedown", handleClickOut);

        setIsLoaded(true);

        return () => {
            setUnmounted(true);
            document.removeEventListener("mousedown", handleClickOut);
        };
    }, []);

    useEffect(() => {
        const isDifferent = props.multiple ? props.defaultValue !== state.selections : props.defaultValue !== state.selected;

        if (isDifferent && (props.defaultValue || props.defaultValue === "")) {
            dispatch({
                type: "SET_DEFAULT_VALUE",
                multiple: props.multiple,
                updatedValue: props.defaultValue,
                resetOptions: !props.options.length && (!props.multiSelect || !props.options.length),
            });
        }
    }, [props.defaultValue]);

    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        const isMatch = props.multiple ? checkMultiMatch(props.defaultValue, state.selections) : checkSingleMatch(props.defaultValue, state.selected);

        if (!isMatch) {
            const value = props.multiple ? state.selections : state.selected;
            props.updateOnChange(props.name, value);
        }
    }, [state.selections, state.selected]);

    // search by search string text
    useEffect(() => {
        if (state.allowSearch) {
            const searchTimer = setTimeout(async () => {
                dispatch({ type: "SET_SEARCH_START" });
                search(state.searchString).then((options) => {
                    dispatch({ type: "SET_SEARCH_SUCCESS", options: options || [] });
                    setShowDropdown(true);
                });
            }, 350);

            return () => {
                clearTimeout(searchTimer);
            };
        }
    }, [state.searchString]);

    useEffect(() => {
        if (state.options) {
            if (inputRef.current) {
                inputRef.current.addEventListener("keydown", handleKeyDown);
            }

            return () => {
                if (inputRef.current) {
                    inputRef.current.removeEventListener("keydown", handleKeyDown);
                }
            };
        }
    }, [state.activeIndex]);

    useEffect(() => {
        if (props.options && props.options.length) {
            dispatch({ type: "SET_OPTIONS", options: props.options });
        }
    }, [props.options]);

    const search = async (searchString) => {
        let options = [];

        if (props.provider) {
            if (searchString !== "") {
                const fetchedOptions = await getProviderByTerm(searchString, props.provider);

                if (fetchedOptions) {
                    options = fetchedOptions;

                    if (inputRef.current && inputRef.current.value === "") {
                        if (!props.options.length) {
                            return [];
                        }
                    }
                }
            }

            return options;
        }

        if (props.options && props.options.length) {
            options = searchString ? props.options.filter((option) => option.text.toLowerCase().includes(searchString.toLowerCase())) : props.options;

            return options;
        }

        if (props.loadOptions && props.loadOptions.length) {
            if (searchString !== "") {
                options = await props.loadOptions(searchString);
            }

            return options;
        }
    };

    const select = (selectedOption) => {
        // Set Focus to input
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }

        if (props.multiSelect) {
            props.updateOnChange(props.name, selectedOption);

            return;
        }

        setShowDropdown(false);

        const isMatch = props.multiple ? checkMultiContains(selectedOption, state.selections) : checkSingleMatch(selectedOption, state.selected);

        if (isMatch) {
            if (props.multiple) {
                dispatch({ type: "SET_SEARCH_STRING", searchString: "", allowSearch: true });
            }

            notify.warning("Value already added");

            return;
        }

        // Dispatch Options
        const dispatchType = props.multiple ? "SET_SELECTIONS" : "SET_SELECTED";
        const dispatchOptions = {};
        dispatchOptions.searchString = props.multiple || props.clearOnSelect ? "" : selectedOption.text;
        dispatchOptions.updatedValue = selectedOption;
        dispatchOptions.resetOptions = !props.options.length && (!props.multiSelect || !props.options.length);

        dispatch({ type: dispatchType, ...dispatchOptions, multiple: props.multiple });
    };

    const handleBlur = () => {
        if (unmounted || (dropdownRef && dropdownRef.current) || state.searchString === state.selected.text) {
            return;
        }

        setShowDropdown(false);

        if (props.multiSelect) {
            return;
        }

        const option = findOption(state.searchString, state.options);

        if (option && option.text) {
            select(option);
        } else if (!props.multiple) {
            dispatch({ type: "REMOVE_SELECTED_OPTION" });
        }

        if (inputRef.current) {
            inputRef.current.removeEventListener("blur", handleBlur);
        }
    };
    const handleClickOut = (event) => {
        if (!typeaheadRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };

    const handleInputClick = async (event) => {
        if (props.disabled) {
            return;
        }

        event.currentTarget.select();

        if (state.options.length) {
            setShowDropdown(true);

            return;
        }

        if (state.searchString !== "" && !state.options.length) {
            dispatch({ type: "SET_SEARCH_START" });
            search(state.searchString).then((options) => {
                dispatch({ type: "SET_SEARCH_SUCCESS", options: options || [] });
                dispatch({ type: "SET_FETCHED", fetched: false });
                setShowDropdown(true);
            });
        }
    };
    const handleInputChange = (event) => {
        inputRef.current.focus();

        const searchString = event.target.value;
        dispatch({ type: "SET_SEARCH_STRING", searchString, allowSearch: true });
    };

    const handleArrowUpClick = () => {
        setShowDropdown(false);
    };

    const handleArrowDownClick = () => {
        setShowDropdown(true);
    };

    const handleOptionSelect = (option) => {
        if (option) {
            select(option);
        }
    };

    const handleRemoveSelected = () => {
        setShowDropdown(false);
        dispatch({ type: "REMOVE_SELECTED_OPTION" });
        inputRef.current.focus();
    };

    const handleRemoveTagClick = (option) => {
        dispatch({ type: "REMOVE_SELECTION", option, dataType: props.dataType });
        inputRef.current.focus();
    };

    const handleKeyDown = (event) => {
        switch (event.keyCode) {
            case 9: // tab
                setShowDropdown(false);
                break;
            case 13: // enter
                const option = state.options[state.activeIndex];

                if (option && dropdownRef && dropdownRef.current) {
                    select(option);
                }

                break;
            case 27: // escape key
                setShowDropdown(false);
                break;
            case 38: // up
                setShowDropdown(true);

                if (state.activeIndex > 0) {
                    dispatch({ type: "SET_ACTIVE_INDEX", activeIndex: (state.activeIndex -= 1) });
                } else {
                    dispatch({ type: "SET_ACTIVE_INDEX", activeIndex: state.options.length - 1 });
                }

                break;
            case 40: // down
                setShowDropdown(true);

                if (state.activeIndex < state.options.length - 1) {
                    dispatch({ type: "SET_ACTIVE_INDEX", activeIndex: (state.activeIndex += 1) });
                } else {
                    dispatch({ type: "SET_ACTIVE_INDEX", activeIndex: 0 });
                }

                break;
            default:
                break;
        }
    };

    const loading = state.isFetching ? (
        <span className="loader">
            <Loader natural />
        </span>
    ) : null;

    const icons = !state.options.length ? (
        <FontAwesomeIcon icon={faKeyboard} />
    ) : showDropdown ? (
        <FontAwesomeIcon
            tabIndex="-1"
            icon={faChevronUp}
            onClick={handleArrowUpClick}
        />
    ) : (
        <FontAwesomeIcon
            tabIndex="-1"
            icon={faChevronDown}
            onClick={handleArrowDownClick}
        />
    );

    const showSelected = props.multiple && state.selections && state.selections.length;

    const selected = !!showSelected ? (
        props.draggable ? (
            <SortableList
                selections={state.selections}
                removeValue={handleRemoveTagClick}
                disabled={props.disabled}
                updateOnChange={props.updateOnChange}
                sectionName={props.name}
            />
        ) : (
            state.selections.map((sel) => {
                return (
                    <SelectedItem
                        key={sel.value}
                        value={sel.value}
                        text={sel.text}
                        removeValue={() => handleRemoveTagClick(sel)}
                        disabled={props.disabled}
                    />
                );
            })
        )
    ) : null;

    return (
        <div
            className="typeahead"
            ref={typeaheadRef}
        >
            {selected}
            <div className="typeahead-input">
                <input
                    type="text"
                    id={props.id || props.name}
                    name={props.name}
                    ref={inputRef}
                    value={state.searchString}
                    accessKey={props.accessKey}
                    placeholder={props.placeholder}
                    autoComplete="off"
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    disabled={props.disabled}
                />
                {!props.disabled && state.selected.value && (
                    <span
                        className={`button -text btn-clear`}
                        onClick={handleRemoveSelected}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </span>
                )}
                {!props.disabled && <span className={`button -text btn-menu`}>{state.isFetching ? loading : icons}</span>}
                {showDropdown && (
                    <Dropdown
                        ref={dropdownRef}
                        id={`${props.name}-dropdown`}
                        activeIndex={state.activeIndex}
                        data={state.options}
                        onSelect={handleOptionSelect}
                        creatable={props.creatable}
                        inputValue={state.searchString}
                        provider={props.provider}
                        isFetching={state.isFetching}
                        tags={props.tags}
                        lengthRestriction={props.lengthRestriction}
                    />
                )}
                {!props.provider && !state.options.length && showDropdown && <NoOptions />}
            </div>
        </div>
    );
};

export { Typeahead };
